import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ShieldAlert, ShieldCheck, RefreshCw, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const REASON_STYLE = {
  missing:  "bg-rose-100 text-rose-700 border-rose-200",
  expired:  "bg-rose-100 text-rose-700 border-rose-200",
  expiring: "bg-amber-100 text-amber-700 border-amber-200",
};

function Stat({ label, value, tone = "default", testid }) {
  const cls = tone === "danger" ? "border-rose-200 bg-rose-50/50" : "border-slate-200 bg-white";
  return (
    <div data-testid={testid} className={`rounded-lg border ${cls} p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

export function WWCCGuardPanel() {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [running, setRunning] = useState(false);
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);

  const load = (all = showAll) =>
    api.get(`/wwcc/overview?status=${all ? "all" : "open"}`).then((r) => setData(r.data)).catch(() => {});
  const loadClients = (query = "") =>
    api.get(`/wwcc/clients?q=${encodeURIComponent(query)}`).then((r) => setClients(r.data.items || [])).catch(() => {});

  useEffect(() => { load(); loadClients(); }, []);
  useEffect(() => {
    const t = setTimeout(() => loadClients(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const runCheck = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/wwcc/run-check");
      toast.success(`Checked ${r.jobs_checked} open tasks — ${r.new_flags} new flag(s), ${r.flags_open} open`);
      load();
    } catch { toast.error("Check failed"); }
    setRunning(false);
  };

  const toggleClient = async (name, required) => {
    try {
      await api.put("/wwcc/clients", { client_name: name, required });
      toast.success(`${name}: WWCC ${required ? "required" : "not required"}`);
      loadClients(q); load();
    } catch { toast.error("Update failed"); }
  };

  const resolve = async (id) => {
    try {
      await api.put(`/wwcc/flags/${id}/resolve`, { note: "Resolved manually" });
      toast.success("Flag resolved");
      load();
    } catch { toast.error("Resolve failed"); }
  };

  const flags = data?.flags || [];
  return (
    <div className="space-y-4" data-testid="wwcc-guard-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-rose-600" /> WWCC Guard
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            Checks every open AroFlo task at a WWCC-required client the moment assignments sync in.
            Non-compliant workers and their BU manager are emailed immediately. Runs after every AroFlo sync + nightly.
          </p>
        </div>
        <Button size="sm" data-testid="wwcc-run-check-btn" onClick={runCheck} disabled={running}>
          {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Run check now
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Open flags" value={data?.stats?.open_flags ?? "—"}
              tone={(data?.stats?.open_flags ?? 0) > 0 ? "danger" : "default"} testid="wwcc-stat-open" />
        <Stat label="WWCC-required clients" value={data?.stats?.required_clients ?? "—"} testid="wwcc-stat-clients" />
        <Stat label="Current WWCC holders" value={data?.stats?.wwcc_holders ?? "—"} testid="wwcc-stat-holders" />
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Last check</div>
          <div className="text-xs text-slate-700 mt-1.5">
            {data?.last_check ? (
              <>
                {String(data.last_check.at).slice(0, 16).replace("T", " ")} UTC
                <div className="text-slate-400">{data.last_check.jobs_checked} tasks · trigger: {String(data.last_check.trigger).split(":")[0]}</div>
              </>
            ) : "Never run"}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50/60">
          <span className="text-xs font-medium text-slate-600">Flagged assignments</span>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
            <input type="checkbox" data-testid="wwcc-show-all" checked={showAll}
                   onChange={(e) => { setShowAll(e.target.checked); load(e.target.checked); }} />
            Include resolved
          </label>
        </div>
        {flags.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" /> No non-compliant assignments detected.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-medium px-3 py-2">Worker</th>
                  <th className="text-left font-medium px-3 py-2">Task</th>
                  <th className="text-left font-medium px-3 py-2">Client</th>
                  <th className="text-left font-medium px-3 py-2">Due</th>
                  <th className="text-left font-medium px-3 py-2">Reason</th>
                  <th className="text-left font-medium px-3 py-2">Flagged</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {flags.map((f) => (
                  <tr key={f.id} data-testid={`wwcc-flag-${f.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-slate-800">{f.technician}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{f.job_number || f.job_ref}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{f.client_name}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{(f.due_date || "—").slice(0, 10)}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] ${REASON_STYLE[f.reason] || ""}`}>
                        {f.reason}{f.reason !== "missing" && f.wwcc_expiry ? ` · ${String(f.wwcc_expiry).slice(0, 10)}` : ""}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{String(f.first_seen || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-xs">
                      {f.status === "open"
                        ? <span className="text-rose-600 font-medium">open</span>
                        : <span className="text-slate-400">{f.status.replace("_", " ")}</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {f.status === "open" && (
                        <Button size="sm" variant="outline" className="h-6 text-[11px]"
                                data-testid={`wwcc-resolve-${f.id}`} onClick={() => resolve(f.id)}>
                          Resolve
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-xs font-medium text-slate-600 mb-2">WWCC-required clients</div>
        <div className="flex items-center gap-2 mb-3 border border-slate-200 rounded-md px-2.5 py-1.5 max-w-md">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input data-testid="wwcc-client-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search clients to add or remove…"
                 className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
        </div>
        <div className="space-y-1.5">
          {clients.map((c) => (
            <div key={c.name} className="flex items-center justify-between gap-3 text-sm py-1 border-b border-slate-50">
              <span className="text-slate-700">
                {c.name}
                {c.suggested && !c.required && (
                  <span className="ml-2 text-[10px] text-amber-600">looks child-related — review</span>
                )}
              </span>
              <Switch checked={c.required} data-testid={`wwcc-client-toggle-${c.name.slice(0, 20)}`}
                      onCheckedChange={(v) => toggleClient(c.name, v)} />
            </div>
          ))}
          {clients.length === 0 && <div className="text-xs text-slate-400">No matching clients.</div>}
        </div>
      </div>
    </div>
  );
}

export default WWCCGuardPanel;
